import {
  toChecklistItemOnView,
  toInspectionTemplateItemModel,
} from 'components/dataProviders/withRequiredInspectionTemplate/model';
import { ProcessObject } from 'components/dataProviders/withRequiredInspectionTemplate/types';
import { createInspectionCode } from 'helpers/generators';
import { toLabelledEntities, toLabelledEntity } from 'helpers/misc';
import { logError } from 'setup/logger/logError';
import { CompanyModel } from 'shared/domain/company/types/model';
import { ContractModel } from 'shared/domain/contract/types/model';
import {
  ComplianceCheckModel,
  InspectionModel,
  InspectionStatus,
  ProtocolItemModel,
} from 'shared/domain/inspection/inspectionModel';
import { LevelModel } from 'shared/domain/level/types/model';
import { SiteModel } from 'shared/domain/site/types/model';
import {
  ChecklistItemModel,
  InspectionTemplateModel,
} from 'shared/domain/template/inspectionTemplateModel';
import { modelToViewBase } from 'shared/domain/toBaseView';
import { getUserLabelOptionalByOrganization } from 'shared/domain/user/mapping/toView';
import { HashMap, LabelledEntity } from 'shared/types/commonView';
import { Color } from 'shared/utils/colors';
import { stringToViewDate } from 'shared/utils/date/stringToViewDate';
import {
  ComplianceCheckOnView,
  InspectionOnTableView,
  InspectionOnView,
  ProtocolItemOnTableView,
  ProtocolOnView,
} from './inspectionOnView';
import { FieldValueModel } from 'shared/domain/fieldValue/fieldValueModel';

function findChecklistItem(
  template: InspectionTemplateModel,
  protocolItemModel: ProtocolItemModel
): ChecklistItemModel | null {
  const checklistItem = template.checklist.find((item) => {
    return item._id === protocolItemModel.templateChecklistItem;
  });
  if (!checklistItem) {
    // This can happen when the inspection template was modified after the inspection was created
    // The protocol item will be filtered out from the inspection view
    return null;
  }
  return checklistItem;
}

function toComplianceCheckOnView(
  complianceCheck: ComplianceCheckModel
): ComplianceCheckOnView {
  return {
    ...complianceCheck,
  };
}

function toProtocolOnView(
  template: InspectionTemplateModel,
  inspectionId: string
): (protocolItemModel: ProtocolItemModel) => ProtocolOnView | null {
  return function protocolItem(protocolItemModel): ProtocolOnView | null {
    const checklistItem = findChecklistItem(template, protocolItemModel);

    if (!checklistItem) {
      return null;
    }

    return {
      ...protocolItemModel,
      inspectionId: inspectionId,
      templateChecklistItem: toChecklistItemOnView(template._id)(checklistItem),
      complianceCheck: toComplianceCheckOnView(
        protocolItemModel.complianceCheck
      ),
    };
  };
}

function toProtocolItemOnTableView(
  template: InspectionTemplateModel,
  inspectionId: string
): (
  protocolItemModel: ProtocolItemModel
) => ProtocolItemOnTableView | null {
  return function protocolItemOnTableView(
    protocolItemInDto
  ): ProtocolItemOnTableView | null {
    const checklistItem = findChecklistItem(template, protocolItemInDto);

    if (!checklistItem) {
      return null;
    }

    return {
      _id: protocolItemInDto._id,
      modifiedAt: protocolItemInDto.modifiedAt,
      modifiedBy: protocolItemInDto.modifiedBy,
      inspectionId: inspectionId,
      templateChecklistItem: toChecklistItemOnView(template._id)(checklistItem),
      complianceCheck: toComplianceCheckOnView(
        protocolItemInDto.complianceCheck
      ),
    };
  };
}

export function toInspectionOnView(
  processesObject: ProcessObject,
  template: InspectionTemplateModel,
  inspection: InspectionModel,
  sites: SiteModel[],
  levels: LevelModel[],
  worktypes: FieldValueModel[],
  companies: CompanyModel[],
  contracts: ContractModel[],
  organizationId: string | null
): InspectionOnView {
  const process = template.process;
  const templateOnView = toInspectionTemplateItemModel(
    processesObject,
    template,
    organizationId || ''
  );
  const sitesLabelled: LabelledEntity[] = (inspection.sites ?? []).map(
    (siteId) => {
      const site = sites.find((s) => s._id === siteId);
      return site ? toLabelledEntity(site) : { _id: siteId, label: siteId };
    }
  );
  const controlledParties = toLabelledEntities(
    inspection.controlledParty
      .map((partyId) =>
        companies.find((company) => company._id === partyId)
      )
      .filter((x): x is CompanyModel => Boolean(x))
      .map((company) => ({ _id: company._id, label: company.shortLabel }))
  );
  const inspectionWorktypes = toLabelledEntities(
    inspection.workTypes
      .map((worktypeId) =>
        worktypes.find((worktype) => worktype._id === worktypeId)
      )
      .filter((x): x is FieldValueModel => Boolean(x))
  );
  const onViewBase = modelToViewBase(inspection, organizationId);

  const protocol = inspection.protocol
    .map(toProtocolOnView(template, inspection._id))
    .filter((item): item is ProtocolOnView => item !== null);

  return {
    ...onViewBase,

    isCompleted: inspection.isCompleted,
    comment: inspection.comment,
    sites: sitesLabelled,
    contracts: toLabelledEntities(
      (inspection.contracts ?? [])
        .map((contractId) =>
          contracts.find((contract) => contract._id === contractId)
        )
        .filter((x): x is ContractModel => Boolean(x))
    ),
    controlledParty: controlledParties,
    levels: toLabelledEntities(
      (inspection.levels ?? [])
        .map((levelId) => levels.find((level) => level._id === levelId))
        .filter((x): x is LevelModel => Boolean(x))
    ),
    workTypes: inspectionWorktypes,
    process,
    template: templateOnView,
    protocol,
    sections: inspection.sections ?? [],
    code: createInspectionCode(
      inspection.createdAt,
      inspection._id,
      processesObject[process]?.code || ''
    ),
    status: !inspection.isCompleted
      ? InspectionStatus.inProgress
      : InspectionStatus.completed,
    inspectionDate: stringToViewDate(inspection.inspectionDate),
    author: getUserLabelOptionalByOrganization(
      inspection.createdBy,
      organizationId
    ),
    joinedControlledParty: controlledParties
      .map((party) => party.label)
      .join(', '),
    joinedWorkTypes: inspectionWorktypes
      .map((workType) => workType.label)
      .join(', '),
    renderableProcess: {
      code: processesObject[process]?.code || Color.white,
      color: processesObject[process]?.color || Color.white,
    },
  };
}

export function toInspectionOnTableView(
  processesObject: ProcessObject,
  templatesObject: HashMap<InspectionTemplateModel>,
  inspection: InspectionModel,
  sites: SiteModel[],
  levels: LevelModel[],
  worktypes: FieldValueModel[],
  companies: CompanyModel[],
  contracts: ContractModel[],
  organizationId: string
): InspectionOnTableView {
  const template = templatesObject[inspection.template];
  const process = template.process;
  const templateOnView = toInspectionTemplateItemModel(
    processesObject,
    template,
    organizationId
  );
  const sitesLabelled: LabelledEntity[] = (inspection.sites ?? []).map(
    (siteId) => {
      const site = sites.find((s) => s._id === siteId);
      return site ? toLabelledEntity(site) : { _id: siteId, label: siteId };
    }
  );
  const controlledParties = toLabelledEntities(
    inspection.controlledParty
      .map((partyId) =>
        companies.find((company) => company._id === partyId)
      )
      .filter((x): x is CompanyModel => Boolean(x))
      .map((company) => ({ _id: company._id, label: company.shortLabel }))
  );
  const inspectionWorktypes = toLabelledEntities(
    inspection.workTypes
      .map((worktypeId) =>
        worktypes.find((worktype) => worktype._id === worktypeId)
      )
      .filter((x): x is FieldValueModel => Boolean(x))
  );
  const onViewBase = modelToViewBase(inspection, organizationId);

  const protocol = inspection.protocol
    .map(toProtocolItemOnTableView(template, inspection._id))
    .filter((item): item is ProtocolItemOnTableView => item !== null);

  return {
    ...onViewBase,
    isCompleted: inspection.isCompleted,
    comment: inspection.comment,
    sites: sitesLabelled,
    contracts: toLabelledEntities(
      (inspection.contracts ?? [])
        .map((contractId) =>
          contracts.find((contract) => contract._id === contractId)
        )
        .filter((x): x is ContractModel => Boolean(x))
    ),
    controlledParty: controlledParties,
    levels: toLabelledEntities(
      (inspection.levels ?? [])
        .map((levelId) => levels.find((level) => level._id === levelId))
        .filter((x): x is LevelModel => Boolean(x))
    ),
    workTypes: inspectionWorktypes,
    process,
    template: templateOnView,
    deleted: Boolean(inspection.deleted),
    protocol,
    code: createInspectionCode(
      inspection.createdAt,
      inspection._id,
      processesObject[process]?.code || ''
    ),
    status: !inspection.isCompleted
      ? InspectionStatus.inProgress
      : InspectionStatus.completed,
    // renderableInspectionDate: toStandardDateDisplayStringFormat(
    //   inspection.inspectionDate,
    //   timezone
    // ),
    inspectionDate: stringToViewDate(inspection.inspectionDate),
    author: getUserLabelOptionalByOrganization(
      inspection.createdBy,
      organizationId
    ),
    joinedControlledParty: controlledParties
      .map((party) => party.label)
      .join(', '),
    joinedWorkTypes: inspectionWorktypes
      .map((workType) => workType.label)
      .join(', '),
    renderableProcess: {
      code: processesObject[process]?.code || Color.white,
      color: processesObject[process]?.color || Color.white,
    },
    relatedIssuesCount: inspection.relatedIssuesCount ?? 0,
  };
}
